import {
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  SlidersHorizontal,
  Download,
  FileText,
  MoreVertical,
  Building2,
  Calendar,
  Users,
  MapPin,
  LayoutGrid,
  Clock,
  Mail,
  User,
  FileCheck,
  Image as ImageIcon,
  Tag,
} from "lucide-react";
import Header from "@/components/Header";
import EventsOfficeHeader from "@/components/EventsOfficeHeader";
import AdminHeader from "@/components/AdminHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useCallback, useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { useToast } from "@/hooks/use-toast";
import { getApiBaseUrl } from "@/lib/apiBase";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useLocation } from "wouter";
import DocumentViewer from "@/components/DocumentViewer";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

async function updateVendorStatus(
  requestId: string,
  status: "approved" | "rejected",
  token: string | null
) {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/applications/${requestId}/status`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      }
    );

    // CRITICAL FIX 1: Check Content-Type header before trying to parse the body.
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const text = await response.text();
      // Throw an informative error if the server didn't return JSON.
      throw new Error(
        `Server Error: Expected JSON, but received non-JSON (Status: ${response.status}). Preview: ${text.substring(0, 50)}...`
      );
    }

    // CRITICAL FIX 2: Only parse JSON if we know it's JSON.
    const data = await response.json();

    // CRITICAL FIX 3: Check response status after successfully parsing the body.
    if (!response.ok) {
      throw new Error(data.message || "Failed to update status");
    }

    return data.data; // updated application
  } catch (err: unknown) {
    logger.error(err);
    const errorMessage =
      err instanceof Error ? err.message : "Failed to update status";
    throw new Error(errorMessage);
  }
}

interface VendorRequest {
  _id: string;
  type: "bazaar" | "booth";
  status: "pending" | "approved" | "rejected" | "cancelled";
  boothSize: "2x2" | "4x4";
  durationWeeks?: number;
  locationPreference?: string;
  createdAt: string;
  attendees: Array<{ name: string; email: string; individualID?: string }>;
  event?:
    | {
        _id: string;
        name: string;
        description?: string;
        startDate?: string;
        endDate?: string;
        location?: string;
      }
    | string;
  createdBy?: {
    companyName?: string;
    email?: string;
    companyLogoUrl?: string;
    taxCardUrl?: string;
    createdAt?: string;
  };
}

//todo: remove mock functionality
// const vendorRequests = [
//   {
//     id: "1",
//     company: "Tech Solutions Co.",
//     bazaar: "Spring Festival Bazaar",
//     boothSize: "4x4",
//     attendees: 3,
//     status: "pending",
//   },
//   {
//     id: "2",
//     company: "Food Delights",
//     bazaar: "Summer Market",
//     boothSize: "2x2",
//     attendees: 2,
//     status: "pending",
//   },
// ];

export default function VendorRequests() {
  const [requests, setRequests] = useState<VendorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState<VendorRequest | null>(null);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<{
    url: string;
    title: string;
    filename?: string;
  } | null>(null);
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string[]>(["all"]);
  const [, setLocation] = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Initialize token and userRole on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    setToken(storedToken);

    if (storedToken) {
      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        setUserRole(payload?.role || null);
      } catch (err) {
        logger.error("Failed to decode token:", err);
        setUserRole(null);
      }
    } else {
      setUserRole(null);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${getApiBaseUrl()}/api/applications`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(
          `Server Error: Expected JSON, got non-JSON (Status: ${res.status}). Preview: ${text.substring(0, 50)}...`
        );
      }

      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.message || "Failed to fetch applications");
      }

      const apps = Array.isArray(body.data) ? body.data : [];
      setRequests(apps);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to load applications"
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return; // Wait for token to be loaded
    fetchApplications();
  }, [fetchApplications, token]);

  // Helper function to get event name from request
  const getEventName = (request: VendorRequest): string => {
    // If event is populated (object with name property)
    if (
      request?.event &&
      typeof request.event === "object" &&
      request.event.name
    ) {
      return request.event.name;
    }

    // For platform booth applications that are pending (no event created yet),
    // generate the event name from the vendor's information
    if (request?.type === "booth" && !request.event) {
      const vendorName = request?.createdBy?.companyName || "Vendor";
      return `${vendorName}'s platform booth`;
    }

    // If event is just an ID string or null/undefined, return -
    return "-";
  };

  const handleApprove = async (requestId: string) => {
    if (!token) {
      toast({
        title: "Error",
        description: "Authentication token is missing.",
        variant: "destructive",
      });
      return null;
    }

    try {
      const updatedApp = await updateVendorStatus(requestId, "approved", token);
      if (updatedApp) {
        // Refresh the applications list to get the latest data
        await fetchApplications();
        toast({
          title: "Request approved",
          description: "The vendor request has been approved.",
        });
        return updatedApp;
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to approve the request.",
        variant: "destructive",
      });
    }
    return null;
  };

  const handleReject = async (requestId: string) => {
    if (!token) {
      toast({
        title: "Error",
        description: "Authentication token is missing.",
        variant: "destructive",
      });
      return null;
    }

    try {
      const updatedApp = await updateVendorStatus(requestId, "rejected", token);
      if (updatedApp) {
        // Refresh the applications list to get the latest data
        await fetchApplications();
        toast({
          title: "Request rejected",
          description: "The vendor request has been rejected.",
          variant: "destructive",
        });
        return updatedApp;
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to reject the request.",
        variant: "destructive",
      });
    }
    return null;
  };

  const handleGoToPolls = () => {
    setLocation("/events-office/polls");
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string, checked: boolean) => {
    let newStatuses: string[];

    if (status === "all") {
      newStatuses = checked ? ["all"] : [];
    } else {
      if (checked) {
        newStatuses = statusFilter.filter((s) => s !== "all").concat(status);
      } else {
        newStatuses = statusFilter.filter((s) => s !== status);
        if (newStatuses.length === 0) {
          newStatuses = ["all"];
        }
      }
    }

    setStatusFilter(newStatuses);
  };

  const handleViewDocuments = (requestId: string) => {
    const req = requests.find((r) => r._id === requestId);
    setSelected(req || null);
    setDetailsOpen(!!req);
  };

  const handleViewDocument = (
    url: string,
    title: string,
    filename?: string
  ) => {
    setViewingDocument({ url, title, filename });
    setDocumentViewerOpen(true);
  };

  const handleDownloadDocument = async (url: string, filename?: string) => {
    try {
      // Fetch the file as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      const blob = await response.blob();

      // Extract filename from URL, handling query parameters
      const urlPath = url.split("?")[0]; // Remove query parameters
      const defaultFilename = urlPath.split("/").pop() || "document";
      const downloadFilename = filename || defaultFilename;

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      toast({
        title: "Download successful",
        description: "ID card downloaded successfully.",
      });
    } catch {
      toast({
        title: "Download failed",
        description: "Failed to download the document. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {userRole === "admin" ? (
        <AdminHeader />
      ) : userRole === "events_office" ? (
        <EventsOfficeHeader />
      ) : (
        <Header />
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Vendor Requests</h1>
              <p className="text-muted-foreground">
                Review vendor participation requests for bazaars and booths
              </p>
            </div>
            {userRole === "events_office" && (
              <Button
                onClick={handleGoToPolls}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-2 text-sm font-semibold rounded-md shadow-sm whitespace-nowrap"
              >
                <Plus className="mr-1 h-4 w-4" />
                Create Poll
              </Button>
            )}
          </div>
        </div>

        {/* Sidebar Layout with Filters */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-32">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <SlidersHorizontal className="h-5 w-5" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Status Filter */}
                  <div className="space-y-2">
                    <div className="text-sm font-semibold">Status</div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="status-all"
                          checked={statusFilter.includes("all")}
                          onCheckedChange={(checked) =>
                            handleStatusFilterChange("all", checked as boolean)
                          }
                        />
                        <Label
                          htmlFor="status-all"
                          className="cursor-pointer text-sm"
                        >
                          All
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="status-pending"
                          checked={statusFilter.includes("pending")}
                          onCheckedChange={(checked) =>
                            handleStatusFilterChange(
                              "pending",
                              checked as boolean
                            )
                          }
                        />
                        <Label
                          htmlFor="status-pending"
                          className="cursor-pointer text-sm"
                        >
                          Pending
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="status-approved"
                          checked={statusFilter.includes("approved")}
                          onCheckedChange={(checked) =>
                            handleStatusFilterChange(
                              "approved",
                              checked as boolean
                            )
                          }
                        />
                        <Label
                          htmlFor="status-approved"
                          className="cursor-pointer text-sm"
                        >
                          Approved
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="status-rejected"
                          checked={statusFilter.includes("rejected")}
                          onCheckedChange={(checked) =>
                            handleStatusFilterChange(
                              "rejected",
                              checked as boolean
                            )
                          }
                        />
                        <Label
                          htmlFor="status-rejected"
                          className="cursor-pointer text-sm"
                        >
                          Rejected
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setStatusFilter(["all"]);
                    }}
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Right Content - Vendor Requests Table */}
          <div className="flex-1">
            <Card>
              <CardHeader>
                <CardTitle>Vendor Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading requests...
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-red-600">{error}</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company Name</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Booth Size</TableHead>
                        <TableHead>Attendees</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests
                        .filter(
                          (r) =>
                            statusFilter.includes("all") ||
                            statusFilter.includes(r.status)
                        )
                        .map((request) => (
                          <TableRow
                            key={request._id}
                            data-testid={`row-request-${request._id}`}
                          >
                            <TableCell className="font-medium">
                              {request?.createdBy?.companyName || "Unknown"}
                            </TableCell>
                            <TableCell>{getEventName(request)}</TableCell>
                            <TableCell>{request?.boothSize || "-"}</TableCell>
                            <TableCell>
                              {Array.isArray(request?.attendees)
                                ? request.attendees.length
                                : 0}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  request.status === "approved"
                                    ? "bg-green-100 text-green-800 border-green-200/50 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800/50"
                                    : request.status === "rejected"
                                      ? "bg-red-100 text-red-800 border-red-200/50 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50"
                                      : "bg-yellow-100 text-yellow-800 border-yellow-200/50 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800/50"
                                }
                              >
                                {request.status?.charAt(0).toUpperCase() +
                                  request.status?.slice(1)}
                              </Badge>
                            </TableCell>

                            <TableCell>
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleViewDocuments(request._id)
                                  }
                                  data-testid={`button-view-${request._id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {request.status === "pending" && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white focus-visible:ring-0 focus:outline-none border-0"
                                      onClick={() => handleApprove(request._id)}
                                      data-testid={`button-approve-${request._id}`}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleReject(request._id)}
                                      data-testid={`button-reject-${request._id}`}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-xl p-0 overflow-hidden bg-background text-foreground border-border shadow-2xl">
            <TooltipProvider delayDuration={500} skipDelayDuration={0}>
              {selected && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selected._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="flex flex-col max-h-[85vh]"
                  >
                    {/* Header */}
                    <div className="relative p-6 pb-2 shrink-0">
                      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none" />
                      <div className="relative z-10 space-y-1 pr-12">
                        <div className="flex flex-wrap items-center gap-3">
                          <DialogTitle className="text-2xl md:text-3xl font-bold leading-tight">
                            {selected?.createdBy?.companyName ||
                              "Vendor Application"}
                          </DialogTitle>
                          <div className="mt-1">
                            <Badge
                              variant="outline"
                              className={
                                selected.status === "approved"
                                  ? "bg-green-100 text-green-800 border-green-200/50 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800/50"
                                  : selected.status === "rejected"
                                    ? "bg-red-100 text-red-800 border-red-200/50 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50"
                                    : "bg-yellow-100 text-yellow-800 border-yellow-200/50 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800/50"
                              }
                            >
                              {selected.status?.charAt(0).toUpperCase() +
                                selected.status?.slice(1)}
                            </Badge>
                          </div>
                        </div>
                        {selected?.createdBy?.email && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                            <Mail className="w-4 h-4 text-primary" />
                            <span>{selected.createdBy.email}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div
                      className="
                      overflow-y-auto p-6 pt-2 space-y-6 pr-4
                      [&::-webkit-scrollbar]:w-1.5
                      [&::-webkit-scrollbar-track]:bg-transparent
                      [&::-webkit-scrollbar-thumb]:bg-gray-200
                      [&::-webkit-scrollbar-thumb]:rounded-full
                      dark:[&::-webkit-scrollbar-thumb]:bg-[#6A33B8]/10
                      dark:[&::-webkit-scrollbar-thumb]:hover:bg-[#6A33B8]/40
                      transition-colors
                    "
                    >
                      {/* Company Logo */}
                      {selected?.createdBy?.companyLogoUrl && (
                        <div className="flex justify-center">
                          <img
                            src={selected.createdBy.companyLogoUrl}
                            alt="Company Logo"
                            className="w-24 h-24 rounded-lg border-2 border-purple-200 dark:border-[#6A33B8]/30 object-cover shadow-md"
                          />
                        </div>
                      )}

                      {/* Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Event */}
                        <VendorDetailCard
                          icon={Building2}
                          label="Event"
                          value={getEventName(selected)}
                        />

                        {/* Type */}
                        <VendorDetailCard
                          icon={Tag}
                          label="Type"
                          value={
                            selected?.type
                              ? selected.type.charAt(0).toUpperCase() +
                                selected.type.slice(1)
                              : "-"
                          }
                        />

                        {/* Booth Size */}
                        <VendorDetailCard
                          icon={LayoutGrid}
                          label="Booth Size"
                          value={selected?.boothSize || "-"}
                        />

                        {/* Duration */}
                        {selected?.durationWeeks && (
                          <VendorDetailCard
                            icon={Clock}
                            label="Duration"
                            value={`${selected.durationWeeks} week${selected.durationWeeks > 1 ? "s" : ""}`}
                          />
                        )}

                        {/* Location Preference */}
                        {selected?.locationPreference && (
                          <VendorDetailCard
                            icon={MapPin}
                            label="Location Preference"
                            value={selected.locationPreference}
                          />
                        )}

                        {/* Created At */}
                        <VendorDetailCard
                          icon={Calendar}
                          label="Created At"
                          value={
                            selected?.createdAt
                              ? new Date(selected.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )
                              : "-"
                          }
                        />
                      </div>

                      {/* Documents Section */}
                      <div className="space-y-3">
                        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-purple-100">
                          <FileText className="w-4 h-4 text-[#6A33B8] dark:text-[#A78BFA]" />
                          Documents
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Company Logo Document */}
                          <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-[#6A33B8]/20 bg-slate-50/50 dark:bg-[#130F19]">
                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-[#6A33B8]/20 text-[#6A33B8] dark:text-[#D6BCFA] shrink-0">
                              <ImageIcon size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-slate-500 dark:text-purple-300/60 uppercase tracking-wide mb-1">
                                Company Logo
                              </p>
                              {selected?.createdBy?.companyLogoUrl ? (
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-slate-600 dark:text-purple-200/60">
                                    Uploaded{" "}
                                    {selected?.createdBy?.createdAt
                                      ? new Date(
                                          selected.createdBy.createdAt
                                        ).toLocaleDateString("en-US", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        })
                                      : "-"}
                                  </div>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={() => {
                                          if (
                                            !selected?.createdBy?.companyLogoUrl
                                          )
                                            return;
                                          const urlPath =
                                            selected.createdBy.companyLogoUrl.split(
                                              "?"
                                            )[0];
                                          const extension =
                                            urlPath.split(".").pop() || "jpg";
                                          const companyName =
                                            selected.createdBy.companyName ||
                                            "Vendor";
                                          handleViewDocument(
                                            selected.createdBy.companyLogoUrl,
                                            `Company Logo - ${companyName}`,
                                            `${companyName.replace(/\s+/g, "_")}_Logo.${extension}`
                                          );
                                        }}
                                      >
                                        <Eye className="mr-2 h-4 w-4" />
                                        View file
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={() => {
                                          if (
                                            !selected?.createdBy?.companyLogoUrl
                                          )
                                            return;
                                          const urlPath =
                                            selected.createdBy.companyLogoUrl.split(
                                              "?"
                                            )[0];
                                          const extension =
                                            urlPath.split(".").pop() || "jpg";
                                          const companyName =
                                            selected.createdBy.companyName ||
                                            "company";
                                          handleDownloadDocument(
                                            selected.createdBy.companyLogoUrl,
                                            `${companyName.replace(/\s+/g, "_")}_Logo.${extension}`
                                          );
                                        }}
                                      >
                                        <Download className="mr-2 h-4 w-4" />
                                        Download file
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  Not provided
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Tax Card Document */}
                          <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-[#6A33B8]/20 bg-slate-50/50 dark:bg-[#130F19]">
                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-[#6A33B8]/20 text-[#6A33B8] dark:text-[#D6BCFA] shrink-0">
                              <FileCheck size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-slate-500 dark:text-purple-300/60 uppercase tracking-wide mb-1">
                                Tax Card
                              </p>
                              {selected?.createdBy?.taxCardUrl ? (
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-slate-600 dark:text-purple-200/60">
                                    Uploaded{" "}
                                    {selected?.createdBy?.createdAt
                                      ? new Date(
                                          selected.createdBy.createdAt
                                        ).toLocaleDateString("en-US", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        })
                                      : "-"}
                                  </div>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={() => {
                                          if (!selected?.createdBy?.taxCardUrl)
                                            return;
                                          const urlPath =
                                            selected.createdBy.taxCardUrl.split(
                                              "?"
                                            )[0];
                                          const extension =
                                            urlPath.split(".").pop() || "pdf";
                                          const companyName =
                                            selected.createdBy.companyName ||
                                            "Vendor";
                                          handleViewDocument(
                                            selected.createdBy.taxCardUrl,
                                            `Tax Card - ${companyName}`,
                                            `${companyName.replace(/\s+/g, "_")}_TaxCard.${extension}`
                                          );
                                        }}
                                      >
                                        <Eye className="mr-2 h-4 w-4" />
                                        View file
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={() => {
                                          if (!selected?.createdBy?.taxCardUrl)
                                            return;
                                          const urlPath =
                                            selected.createdBy.taxCardUrl.split(
                                              "?"
                                            )[0];
                                          const extension =
                                            urlPath.split(".").pop() || "pdf";
                                          const companyName =
                                            selected.createdBy.companyName ||
                                            "company";
                                          handleDownloadDocument(
                                            selected.createdBy.taxCardUrl,
                                            `${companyName.replace(/\s+/g, "_")}_TaxCard.${extension}`
                                          );
                                        }}
                                      >
                                        <Download className="mr-2 h-4 w-4" />
                                        Download file
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  Not provided
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Attendees Section */}
                      {Array.isArray(selected?.attendees) &&
                        selected.attendees.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-purple-100">
                              <Users className="w-4 h-4 text-[#6A33B8] dark:text-[#A78BFA]" />
                              Attendees ({selected.attendees.length})
                            </h4>
                            <div className="space-y-2">
                              {selected.attendees.map((attendee, index) => (
                                <div
                                  key={`${attendee.name}-${attendee.email}-${index}`}
                                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-[#130F19] border border-slate-200 dark:border-[#6A33B8]/20"
                                >
                                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-[#6A33B8]/20 text-purple-600 dark:text-[#D6BCFA] flex items-center justify-center shrink-0">
                                    <User size={16} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-slate-900 dark:text-purple-50">
                                      {attendee.name}
                                    </div>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="text-xs text-slate-600 dark:text-purple-200/60 truncate">
                                            {attendee.email}
                                          </div>
                                        </TooltipTrigger>
                                        {attendee.email.length > 30 && (
                                          <TooltipContent>
                                            <p>{attendee.email}</p>
                                          </TooltipContent>
                                        )}
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                  {attendee.individualID ? (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          const urlPath =
                                            attendee.individualID!.split(
                                              "?"
                                            )[0];
                                          const extension =
                                            urlPath.split(".").pop() || "jpg";
                                          handleViewDocument(
                                            attendee.individualID!,
                                            `ID Card - ${attendee.name}`,
                                            `${attendee.name.replace(/\s+/g, "_")}_ID.${extension}`
                                          );
                                        }}
                                        className="h-8"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        View
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          const urlPath =
                                            attendee.individualID!.split(
                                              "?"
                                            )[0];
                                          const extension =
                                            urlPath.split(".").pop() || "jpg";
                                          handleDownloadDocument(
                                            attendee.individualID!,
                                            `${attendee.name.replace(/\s+/g, "_")}_ID.${extension}`
                                          );
                                        }}
                                        className="h-8"
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        Download
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <FileText className="h-3 w-3" />
                                      No ID
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    {selected?.status === "pending" && (
                      <div className="p-6 pt-4 border-t border-slate-200 dark:border-[#6A33B8]/20 flex gap-3 justify-end">
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white focus-visible:ring-0 focus:outline-none border-0"
                          onClick={async () => {
                            const updatedApp = await handleApprove(
                              selected._id
                            );
                            if (updatedApp) {
                              setDetailsOpen(false);
                              // fetchApplications is already called in handleApprove
                            }
                          }}
                          data-testid={`dialog-approve-${selected._id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={async () => {
                            const updatedApp = await handleReject(selected._id);
                            if (updatedApp) {
                              setSelected({ ...selected, status: "rejected" });
                              setDetailsOpen(false);
                            }
                          }}
                          data-testid={`dialog-reject-${selected._id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </TooltipProvider>
          </DialogContent>
        </Dialog>

        {/* Document Viewer Dialog */}
        {viewingDocument && (
          <DocumentViewer
            open={documentViewerOpen}
            onOpenChange={setDocumentViewerOpen}
            url={viewingDocument.url}
            title={viewingDocument.title}
            filename={viewingDocument.filename}
          />
        )}
      </main>
    </div>
  );
}

// Helper component for vendor detail cards
function VendorDetailCard({
  icon: Icon,
  label,
  value,
  className = "",
}: {
  icon: any;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-[#6A33B8]/20 bg-slate-50/50 dark:bg-[#130F19] ${className}`}
    >
      <div className="p-2 rounded-lg bg-purple-100 dark:bg-[#6A33B8]/20 text-[#6A33B8] dark:text-[#D6BCFA] shrink-0">
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-500 dark:text-purple-300/60 uppercase tracking-wide mb-0.5">
          {label}
        </p>
        <p className="text-sm font-medium text-slate-900 dark:text-purple-50 break-words">
          {value}
        </p>
      </div>
    </div>
  );
}
